import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  InputAdornment,
  Divider,
} from "@mui/material";

import {
  Search,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
} from "@mui/icons-material";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  getDoc,
  doc,
} from "firebase/firestore";

import { db, auth } from "../../firebase";

function Incomeexpreport() {
  const [loading, setLoading] = useState(true);

  const [schoolId, setSchoolId] = useState("");

  const [incomeList, setIncomeList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);

  const [search, setSearch] = useState("");

  const [typeFilter, setTypeFilter] = useState("All");

  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ================= INITIAL =================
  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      const userSnap = await getDoc(
        doc(db, "users", auth.currentUser.uid)
      );

      const sId = userSnap.data()?.schoolId;

      setSchoolId(sId);

      if (sId) {
        await Promise.all([
          fetchIncome(sId),
          fetchExpenses(sId),
        ]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ================= FETCH INCOME =================
  const fetchIncome = async (sId) => {
    try {
      const q = query(
        collection(db, "income"),
        where("schoolId", "==", sId),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        type: "Income",
        ...d.data(),
      }));

      setIncomeList(data);
    } catch (e) {
      console.error(e);
    }
  };

  // ================= FETCH EXPENSE =================
  const fetchExpenses = async (sId) => {
    try {
      const q = query(
        collection(db, "expenses"),
        where("schoolId", "==", sId),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        type: "Expense",
        ...d.data(),
      }));

      setExpenseList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ================= MERGE DATA =================
  const combinedData = useMemo(() => {
    return [...incomeList, ...expenseList];
  }, [incomeList, expenseList]);

  // ================= FILTER DATA =================
  const filteredData = useMemo(() => {
    return combinedData.filter((item) => {
      const itemDate = item.date || "";

      const matchesSearch =
        item.personName
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        item.description
          ?.toLowerCase()
          .includes(search.toLowerCase());

      const matchesType =
        typeFilter === "All"
          ? true
          : item.type === typeFilter;

      const matchesDate =
        (!fromDate || itemDate >= fromDate) &&
        (!toDate || itemDate <= toDate);

      return matchesSearch && matchesType && matchesDate;
    });
  }, [
    combinedData,
    search,
    typeFilter,
    fromDate,
    toDate,
  ]);

  // ================= TOTALS =================
  const totalIncome = incomeList.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const totalExpense = expenseList.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const balance = totalIncome - totalExpense;

  if (loading) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={1.5} bgcolor="#f8fafc" minHeight="100vh">
      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background:
            "linear-gradient(135deg,#1e3a8a,#2563eb)",
          color: "white",
        }}
      >
        <Typography variant="h5" fontWeight={800}>
          Income & Expense Report
        </Typography>

        <Typography variant="body2">
          Complete financial overview
        </Typography>
      </Paper>

      {/* ================= SUMMARY ================= */}
      <Grid className="row" spacing={2} mb={2}>
        <Grid className="col-sm-4 mb-3" item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={2}>
              <TrendingUp color="success" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Income
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="success.main"
                >
                  ₹{totalIncome}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid className="col-sm-4 mb-3" item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={2}>
              <TrendingDown color="error" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Expense
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="error.main"
                >
                  ₹{totalExpense}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid className="col-sm-4 mb-3" item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={2}>
              <AccountBalanceWallet color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Balance
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                  color={
                    balance >= 0
                      ? "primary.main"
                      : "error.main"
                  }
                >
                  ₹{balance}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* ================= FILTERS ================= */}
      <Card
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
        }}
      >
        <Grid className="row" spacing={2}>
          <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
            <TextField size="small"
              fullWidth
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={2}>
            <TextField size="small"
              select
              fullWidth
              label="Type"
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value)
              }
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Income">
                Income
              </MenuItem>
              <MenuItem value="Expense">
                Expense
              </MenuItem>
            </TextField>
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField size="small"
              type="date"
              fullWidth
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) =>
                setFromDate(e.target.value)
              }
            />
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField size="small"
              type="date"
              fullWidth
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) =>
                setToDate(e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Card>

      {/* ================= TABLE ================= */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
        }}
      >
        <Table>
          <TableHead
            sx={{
              bgcolor: "#eff6ff",
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                Sr No
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Date
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Type
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Person / Source
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Description
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Amount
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 5 }}
                >
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                .map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      {item.date}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={item.type}
                        size="small"
                        color={
                          item.type === "Income"
                            ? "success"
                            : "error"
                        }
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {item.personName}
                    </TableCell>

                    <TableCell
                      sx={{
                        maxWidth: 250,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.description}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color:
                          item.type === "Income"
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      ₹{item.amount}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        <Divider />

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) =>
            setPage(newPage)
          }
          onRowsPerPageChange={(e) => {
            setRowsPerPage(
              parseInt(e.target.value, 10)
            );
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}

export default Incomeexpreport;